// Missietimer: tijdstempel-gebaseerd en bewaard in `gameon_active_timer_v1` (zelfde vorm als de oude app).
// Port van legacy startTimer/pauseTimer/resumeTimer/completeTimer/restoreActiveTimer/setupTimerLifecycle.
// - Tikt elke seconde (uitgelijnd op de secondegrens) + herberekent bij visibilitychange/pageshow/focus.
// - Verlopen terwijl de app dicht was → één keer loggen met de geplande duur + melding.
// - "Klaar" logt de werkelijk gespeelde minuten. Dubbele afronding wordt voorkomen.
// - Afronden: trillen, korte piep, toast, score +1, notificatie (alleen als al toegestaan en app verborgen).
// - Scherm blijft aan (Wake Lock) zolang de timer loopt; documenttitel toont de resterende tijd.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  elapsedMinutes,
  formatClock,
  isExpired,
  parseStoredTimer,
  pauseTimer,
  remainingMs as timerRemainingMs,
  resumeTimer,
  startTimer,
} from '../domain/timer';
import type { ActiveTimer, TimerActivity } from '../domain/types';
import { KEYS } from '../storage/keys';
import { getRaw, removeKey, setJSON } from '../storage/localStore';
import { useActivities } from './ActivitiesProvider';
import type { TimerApi } from './contracts';
import { useLogs } from './LogsProvider';
import { useToast, useToastLayout } from './ToastProvider';

const TimerContext = createContext<TimerApi | null>(null);

type FinishKind = 'manual' | 'auto' | 'closed';

type AudioCtor = typeof AudioContext;
function getAudioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function readStoredTimer(): ActiveTimer | null {
  return parseStoredTimer(getRaw(KEYS.activeTimer));
}

export function TimerProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const { setTimerActive } = useToastLayout();
  const { addLog } = useLogs();
  const { activities, loading: activitiesLoading, incrementScore } = useActivities();

  const [timer, setTimerState] = useState<ActiveTimer | null>(readStoredTimer);
  const [now, setNow] = useState(() => Date.now());
  const [ready, setReady] = useState(false);
  const timerRef = useRef(timer);
  const completingRef = useRef(false);
  const audioRef = useRef<AudioContext | null>(null);
  const baseTitleRef = useRef<string | null>(null);

  // Laatste versies van afhankelijkheden zonder callbacks steeds opnieuw te maken
  const depsRef = useRef({ addLog, incrementScore, activities, showToast });
  useEffect(() => {
    depsRef.current = { addLog, incrementScore, activities, showToast };
  });

  const setTimer = useCallback((next: ActiveTimer | null) => {
    timerRef.current = next;
    if (next) setJSON(KEYS.activeTimer, next);
    else removeKey(KEYS.activeTimer);
    setTimerState(next);
    setNow(Date.now());
  }, []);

  /* ---------- Feedback ---------- */
  const primeAudio = useCallback(() => {
    // Moet binnen een gebruikersgebaar gebeuren (autoplay-beleid iOS/Chrome)
    try {
      const Ctor = getAudioCtor();
      if (!Ctor) return;
      if (!audioRef.current) audioRef.current = new Ctor();
      if (audioRef.current.state === 'suspended') audioRef.current.resume().catch(() => {});
    } catch {
      // geen audio
    }
  }, []);

  const playBeep = useCallback(() => {
    try {
      const Ctor = getAudioCtor();
      if (!Ctor) return;
      if (!audioRef.current) audioRef.current = new Ctor();
      const ctx = audioRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const t0 = ctx.currentTime + 0.05;
      [0, 0.22, 0.44].forEach((offset, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = i === 2 ? 1175 : 880;
        gain.gain.setValueAtTime(0.0001, t0 + offset);
        gain.gain.exponentialRampToValueAtTime(0.25, t0 + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + offset + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0 + offset);
        osc.stop(t0 + offset + 0.18);
      });
    } catch {
      // geen audio
    }
  }, []);

  const notifyFinished = useCallback((naam: string) => {
    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      if (document.visibilityState === 'visible') return; // toast is dan voldoende
      const title = 'Missie volbracht!';
      const options: NotificationOptions = {
        body: `"${naam}" is klaar en gelogd in Game Over.`,
        icon: '/pwa-192.png',
        badge: '/pwa-192.png',
        tag: 'gameon-timer',
      };
      if (navigator.serviceWorker?.getRegistration) {
        navigator.serviceWorker
          .getRegistration()
          .then((reg) => {
            if (reg?.showNotification) return reg.showNotification(title, options);
            new Notification(title, options);
          })
          .catch(() => {});
      } else {
        new Notification(title, options);
      }
    } catch {
      // notificaties niet beschikbaar
    }
  }, []);

  const finishFeedback = useCallback(
    (naam: string) => {
      try {
        navigator.vibrate?.([180, 90, 180]);
      } catch {
        // niet ondersteund
      }
      playBeep();
      notifyFinished(naam);
    },
    [playBeep, notifyFinished],
  );

  /* ---------- Afronden ---------- */
  const finish = useCallback(
    async (kind: FinishKind) => {
      if (completingRef.current) return;
      const state = timerRef.current ?? readStoredTimer();
      if (!state) {
        setTimer(null);
        return;
      }
      completingRef.current = true;
      const { addLog: log, incrementScore: inc, activities: list, showToast: toast } = depsRef.current;
      try {
        const t = Date.now();
        const timeIsUp = kind !== 'manual' || timerRemainingMs(state, t) <= 0;
        const planned = Number(state.activity.duurMinuten) || 30;
        const minutes = timeIsUp ? planned : elapsedMinutes(state, t);
        const moment = new Date(timeIsUp ? Math.min(state.endsAt, t) : t).toISOString();

        // Eerst opruimen, zodat een herlaad of tweede trigger niet opnieuw logt
        setTimer(null);
        if (timeIsUp && kind !== 'closed') finishFeedback(state.activity.naam);

        const match = state.activity.id !== null ? list.find((a) => a.id === state.activity.id) : undefined;
        log({
          activityId: state.activity.id,
          naam: state.activity.naam,
          type: state.activity.type,
          duurMinuten: minutes,
          moment,
          energie: state.activity.energie,
          ...(match ? { categorie: match.categorie } : {}),
        });
        if (state.activity.id !== null) await inc(state.activity.id);

        if (kind === 'closed') {
          toast(`Missie afgerond terwijl de app dicht was: "${state.activity.naam}" is gelogd.`, 4500);
        } else if (timeIsUp) {
          toast(`Tijd is om! "${state.activity.naam}" is gelogd in Game Over.`);
        } else {
          toast(`Missie volbracht! "${state.activity.naam}" (${minutes} min) is gelogd in Game Over.`);
        }
      } catch (e) {
        console.error('Kon missie niet afronden:', e);
        toast('Missie kon niet worden gelogd.');
      } finally {
        completingRef.current = false;
      }
    },
    [setTimer, finishFeedback],
  );

  /* ---------- Herstel bij opstarten (na het laden van activiteiten, voor score + categorie) ---------- */
  useEffect(() => {
    if (ready || activitiesLoading) return;
    setReady(true);
    const t = timerRef.current;
    if (t && isExpired(t, Date.now())) void finish('closed');
  }, [ready, activitiesLoading, finish]);

  /* ---------- Tikken ---------- */
  useEffect(() => {
    if (!ready || !timer || timer.pausedRemainingMs !== null) return;
    const rem = timerRemainingMs(timer, Date.now());
    const delay = rem > 0 ? (rem % 1000 || 1000) + 15 : 0;
    const id = setTimeout(() => {
      const t = Date.now();
      setNow(t);
      if (timerRef.current && isExpired(timerRef.current, t)) void finish('auto');
    }, delay);
    return () => clearTimeout(id);
  }, [ready, timer, now, finish]);

  /* ---------- Levenscyclus: terugkeer naar de app, andere tabbladen ---------- */
  useEffect(() => {
    const resync = () => {
      if (!timerRef.current || document.visibilityState !== 'visible') return;
      const t = Date.now();
      setNow(t);
      if (ready && isExpired(timerRef.current, t)) void finish('auto');
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== KEYS.activeTimer) return;
      const next = readStoredTimer();
      timerRef.current = next;
      setTimerState(next);
      setNow(Date.now());
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('pageshow', resync);
    window.addEventListener('focus', resync);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('pageshow', resync);
      window.removeEventListener('focus', resync);
      window.removeEventListener('storage', onStorage);
    };
  }, [ready, finish]);

  /* ---------- Wake Lock zolang de timer loopt ---------- */
  const running = !!timer && timer.pausedRemainingMs === null;
  useEffect(() => {
    if (!running || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;
    const request = async () => {
      if (document.visibilityState !== 'visible' || (lock && !lock.released)) return;
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) {
          l.release().catch(() => {});
          return;
        }
        lock = l;
      } catch {
        // geweigerd of batterijbesparing: negeren
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void request();
    };
    void request();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (lock && !lock.released) lock.release().catch(() => {});
    };
  }, [running]);

  /* ---------- Documenttitel ---------- */
  const remaining = timer ? timerRemainingMs(timer, now) : 0;
  const isPaused = !!timer && timer.pausedRemainingMs !== null;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (timer) {
      if (baseTitleRef.current === null) baseTitleRef.current = document.title || 'GameOn';
      document.title = `${isPaused ? '⏸' : '⏱'} ${formatClock(remaining)} · GameOn`;
    } else if (baseTitleRef.current !== null) {
      document.title = baseTitleRef.current;
      baseTitleRef.current = null;
    }
  }, [timer, isPaused, remaining]);

  /* ---------- Toasts boven de timerbalk ---------- */
  const hasTimer = !!timer;
  useEffect(() => {
    setTimerActive(hasTimer);
  }, [hasTimer, setTimerActive]);

  /* ---------- Acties ---------- */
  const start = useCallback(
    (activity: TimerActivity) => {
      primeAudio();
      setTimer(startTimer(activity, Date.now()));
    },
    [primeAudio, setTimer],
  );

  const pause = useCallback(() => {
    const t = timerRef.current;
    if (!t || t.pausedRemainingMs !== null) return;
    const n = Date.now();
    if (isExpired(t, n)) {
      void finish('auto');
      return;
    }
    setTimer(pauseTimer(t, n));
    showToast('Timer gepauzeerd');
  }, [finish, setTimer, showToast]);

  const resume = useCallback(() => {
    const t = timerRef.current;
    if (!t || t.pausedRemainingMs === null) return;
    primeAudio();
    setTimer(resumeTimer(t, Date.now()));
    showToast('Timer hervat');
  }, [primeAudio, setTimer, showToast]);

  const complete = useCallback(() => {
    void finish('manual');
  }, [finish]);

  const stop = useCallback(() => {
    const had = !!timerRef.current;
    setTimer(null);
    if (had) showToast('Timer gestopt, er is niets gelogd.');
  }, [setTimer, showToast]);

  const api = useMemo<TimerApi>(
    () => ({ timer, remainingMs: remaining, isPaused, start, pause, resume, complete, stop }),
    [timer, remaining, isPaused, start, pause, resume, complete, stop],
  );

  return <TimerContext.Provider value={api}>{children}</TimerContext.Provider>;
}

export function useTimer(): TimerApi {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer moet binnen <TimerProvider> gebruikt worden');
  return ctx;
}
